import { ComponentType, FC, createElement, lazy } from "react"

export type PreloadableComponent<P extends object> = FC<P> & { preload: () => Promise<unknown> }

/**
 * `React.lazy` that can be loaded ahead of rendering. React.lazy suspends on its first render even when the module is
 * already downloaded; once `preload()` has finished, this one renders the component at once. src/index.tsx preloads
 * the page about to be hydrated (pages/index.tsx `preloadPage`), so React takes over the prerendered HTML in one pass
 * instead of leaving the page half-hydrated, where the first context update makes React throw it away (error #421).
 */
export const lazyWithPreload = <P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>
): PreloadableComponent<P> => {
  let loaded: ComponentType<P> | undefined
  const load = () =>
    factory().then(module => {
      loaded = module.default
      return module
    })
  const Lazy = lazy(load)
  const Component: FC<P> = props => createElement((loaded ?? Lazy) as ComponentType<P>, props)
  return Object.assign(Component, { preload: load })
}
